'use client';

import React, { useState, useCallback } from 'react';
import { addMonths, subMonths, addWeeks, subWeeks, addDays, subDays } from 'date-fns';
import { cn } from '@/app/components/lib/utils';
import { CalendarHeader } from './CalendarHeader';
import { CalendarMonthView } from './CalendarMonthView';
import { CalendarWeekView } from './CalendarWeekView';
import { CalendarDayView } from './CalendarDayView';
export const Calendar = ({
  events = [],
  view: controlledView,
  defaultView = 'month',
  onViewChange,
  currentDate: controlledDate,
  defaultDate = new Date(),
  onDateChange,
  selectedDate: controlledSelectedDate,
  onDateSelect,
  onEventClick,
  onAddEvent,
  onSearchClick,
  selectedEventId,
  onSelectEvent,
  className,
  slotProps
}) => {
  // View state (controlled or uncontrolled)
  const [internalView, setInternalView] = useState(defaultView);
  const activeView = controlledView ?? internalView;
  const handleViewChange = useCallback(newView => {
    if (controlledView === undefined) {
      setInternalView(newView);
    }
    onViewChange?.(newView);
  }, [controlledView, onViewChange]);

  // Current Date state (controlled or uncontrolled)
  const [internalDate, setInternalDate] = useState(defaultDate);
  const activeDate = controlledDate ?? internalDate;
  const handleDateChange = useCallback(newDate => {
    if (controlledDate === undefined) {
      setInternalDate(newDate);
    }
    onDateChange?.(newDate);
  }, [controlledDate, onDateChange]);

  // Selected date state (for cell click / jump)
  const [internalSelectedDate, setInternalSelectedDate] = useState(null);
  const activeSelectedDate = controlledSelectedDate !== undefined ? controlledSelectedDate : internalSelectedDate;
  const handleDateSelect = useCallback(date => {
    if (controlledSelectedDate === undefined) {
      setInternalSelectedDate(date);
    }
    handleDateChange(date);
    onDateSelect?.(date);
  }, [controlledSelectedDate, handleDateChange, onDateSelect]);

  // Selected event state for Day View sidebar inspection
  const [internalSelectedEvent, setInternalSelectedEvent] = useState(null);
  const handleSelectEvent = useCallback(event => {
    setInternalSelectedEvent(event);
    onSelectEvent?.(event);
  }, [onSelectEvent]);

  // Navigation handlers based on active view
  const handlePrev = useCallback(() => {
    let nextDate;
    if (activeView === 'month') {
      nextDate = subMonths(activeDate, 1);
    } else if (activeView === 'week') {
      nextDate = subWeeks(activeDate, 1);
    } else {
      nextDate = subDays(activeDate, 1);
    }
    handleDateChange(nextDate);
  }, [activeView, activeDate, handleDateChange]);
  const handleNext = useCallback(() => {
    let nextDate;
    if (activeView === 'month') {
      nextDate = addMonths(activeDate, 1);
    } else if (activeView === 'week') {
      nextDate = addWeeks(activeDate, 1);
    } else {
      nextDate = addDays(activeDate, 1);
    }
    handleDateChange(nextDate);
  }, [activeView, activeDate, handleDateChange]);
  const handleToday = useCallback(() => {
    const today = new Date();
    handleDateChange(today);
    handleDateSelect(today);
  }, [handleDateChange, handleDateSelect]);
  return <div role="application" aria-label="Calendar" {...slotProps?.root} className={cn('isolate flex flex-col overflow-hidden rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 shadow-sm transition-all', activeView === 'month' ? 'h-full min-h-[720px] md:min-h-[880px]' : 'h-[880px]', className, slotProps?.root?.className)}>
            {/* Top Header */}
            <CalendarHeader currentDate={activeDate} view={activeView} onViewChange={handleViewChange} onPrev={handlePrev} onNext={handleNext} onToday={handleToday} onAddEvent={onAddEvent} onSearchClick={onSearchClick} slotProps={slotProps} />

            {/* Main View Area */}
            <main className="flex flex-1 overflow-hidden">
                {activeView === 'month' && <CalendarMonthView currentDate={activeDate} selectedDate={activeSelectedDate} events={events} onDateSelect={handleDateSelect} onEventClick={onEventClick} onAddEvent={onAddEvent} slotProps={slotProps} />}

                {activeView === 'week' && <CalendarWeekView currentDate={activeDate} selectedDate={activeSelectedDate} events={events} onDateSelect={handleDateSelect} onEventClick={onEventClick} onAddEvent={onAddEvent} slotProps={slotProps} />}

                {activeView === 'day' && <CalendarDayView currentDate={activeDate} selectedDate={activeSelectedDate} events={events} selectedEventId={selectedEventId || internalSelectedEvent?.id} onDateSelect={handleDateSelect} onEventClick={onEventClick} onSelectEvent={handleSelectEvent} onAddEvent={onAddEvent} slotProps={slotProps} />}
            </main>
        </div>;
};